import { HelmetProvider } from "react-helmet-async";
import Card from "../UI/Card";
import RequestSupportCard from "../UI/RequestSupportCard";
import classes from "./Metronome.module.css";
import { Helmet } from "react-helmet";
import Metronome from "./metronome/metronome";
import Button from "../UI/Button";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { mainActions } from "../../store";
import BottomMenu from "../BottomMenu/BottomMenu";
import { emptyTapSequence, measureTap } from "./tapTempo";
import usePracticeTimer, { formatPracticeTime } from "./usePracticeTimer";
import MetronomeQuickTour from "./MetronomeQuickTour";

const MetronomePage = () => {
  const dispatch = useDispatch();
  const startingBpm = useSelector((state) => state.metronomeStartingBpm);
  const isRamping = useSelector((state) => state.metronomeRamping);
  const volume = useSelector((state) => state.metronomeVolume);
  const [currentBpm, setCurrentBpm] = useState(startingBpm);
  const currentBpmRef = useRef(startingBpm);
  const [editBpm, setEditBpm] = useState(false);
  const onPlayCallback = useRef(null);
  const onTempoChangeCallback = useRef(null);
  const [audioState, setAudioState] = useState("loading");
  const onRetryAudioCallback = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const practiceTimer = usePracticeTimer();
  const tapSequence = useRef(emptyTapSequence());
  const [tapStatus, setTapStatus] = useState("Tap twice to set tempo");
  const bpmInput = useRef(null);
  const bpmDisplay = useRef(null);
  const restoreBpmFocus = useRef(false);
  const minBpm = 10;
  const maxBpm = 400;

  const resetTaps = () => {
    tapSequence.current = emptyTapSequence();
    setTapStatus("Tap twice to set tempo");
  };

  useEffect(() => () => { tapSequence.current = emptyTapSequence(); }, []);

  useEffect(() => {
    if (editBpm) {
      bpmInput.current?.focus();
      bpmInput.current?.select();
    } else if (restoreBpmFocus.current) {
      restoreBpmFocus.current = false;
      bpmDisplay.current?.focus();
    }
  }, [editBpm]);

  const setPlaybackTempo = (bpm) => {
    currentBpmRef.current = bpm;
    setCurrentBpm(bpm);
    onTempoChangeCallback.current?.(bpm);
  };

  // Also apply preferences loaded while this page is mounted.
  useEffect(() => {
    if (currentBpmRef.current === startingBpm) return;
    tapSequence.current = emptyTapSequence();
    setTapStatus("Tap twice to set tempo");
    setEditBpm(false);
    currentBpmRef.current = startingBpm;
    setCurrentBpm(startingBpm);
    onTempoChangeCallback.current?.(startingBpm);
  }, [startingBpm]);

  const onMetromomeTick = () => {
    if (isRamping) setPlaybackTempo(Math.min(maxBpm, currentBpmRef.current + 0.02));
  };

  const renderCallback = ({ onPlay, onRetryAudio, onTempoChange }) => {
    onPlayCallback.current = onPlay;
    onRetryAudioCallback.current = onRetryAudio;
    onTempoChangeCallback.current = onTempoChange;
  };

  const onMetronomeStartCallback = () => {
    practiceTimer.start();
    resetTaps();
    setIsPlaying(true);
  };
  const onMetronomeStopCallback = () => {
    practiceTimer.stop();
    resetTaps();
    setIsPlaying(false);
  };

  const onAudioStateChange = (state) => {
    if (state !== "ready") practiceTimer.stop();
    setAudioState(state);
  };

  const absSetBpm = (bpm, fromTap = false) => {
    if (!Number.isFinite(bpm)) return;
    let newBpm = bpm;
    if (newBpm < minBpm) {
      newBpm = minBpm;
    } else if (newBpm > maxBpm) {
      newBpm = maxBpm;
    }

    if (!fromTap) resetTaps();
    setEditBpm(false);
    setPlaybackTempo(newBpm);
    dispatch(mainActions.setMetronomeStartingBpm(newBpm));
  };

  const onTapTempo = () => {
    const result = measureTap(tapSequence.current, performance.now());
    tapSequence.current = result.sequence;
    if (result.status) setTapStatus(result.status);
    if (result.bpm !== undefined) absSetBpm(result.bpm, true);
  };

  const incSetBpm = (inc) => absSetBpm(currentBpmRef.current + inc);

  const onBpmKeyDownEvent = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      restoreBpmFocus.current = true;
      absSetBpm(event.target.valueAsNumber);
      setEditBpm(false);
    } else if (event.key === "Escape") {
      event.preventDefault();
      restoreBpmFocus.current = true;
      setEditBpm(false);
    }
  };

  const absBtn = (inc) => {
    return (
      <Button
        key={inc}
        onClick={() => {
          absSetBpm(+inc);
        }}
      >
        {`to ${inc}bpm`}
      </Button>
    );
  };

  const trimBtn = (inc) => {
    return (
      <Button
        key={inc}
        onClick={() => {
          incSetBpm(+inc);
        }}
      >
        {inc >= 0 ? `+${inc}` : `${inc}`}
      </Button>
    );
  };

  return (
    <div className={classes.background}>
      <HelmetProvider>
        <Helmet>
          <meta charset="UTF-8" />
          <title>Guitar Charts - Ramping Metronome</title>
          <meta
            name="description"
            content="Ramping Metronome is a Metronome that allows you to gradually increase the pace of the metronome over time."
          />
        </Helmet>
      </HelmetProvider>

      <Card className={classes.introduction}>
        <h1>Ramping Metronome</h1>
        <p>Develop your guitar playing speed with a metronome that gradually increases its tempo while you practise.</p>
        <p>Set a comfortable starting speed, enable automatic ramping, and concentrate on your playing without repeatedly stopping to adjust the metronome.</p>
        <div><MetronomeQuickTour /></div>
      </Card>
      <Card className={classes.container}>
        <section className={classes.primary} aria-label="Metronome controls">
          <div className={classes.tempoControls} data-tour-target="metronome-tempo">
            <div className={classes.tempo}>
              {editBpm ? (
                <input
                  ref={bpmInput}
                  className={classes.bpmInput}
                  type="number"
                  min={minBpm}
                  max={maxBpm}
                  step="any"
                  aria-label="Tempo (BPM)"
                  defaultValue={Math.round(currentBpm)}
                  onKeyDown={onBpmKeyDownEvent}
                  onBlur={() => setEditBpm(false)}
                />
              ) : (
                <button
                  ref={bpmDisplay}
                  className={classes.bpmButton}
                  aria-describedby="bpm-edit-help"
                  onClick={() => setEditBpm(true)}
                >
                  <b>{Math.round(currentBpm)}bpm</b>
                </button>
              )}
              <span id="bpm-edit-help">Select BPM to edit</span>
            </div>
            <div className={classes.playback}>
              <Button
                disabled={audioState !== "ready"}
                onClick={() => onPlayCallback.current?.()}
              >
                {isPlaying ? "Stop" : "Start"}
              </Button>
              <Button data-tour-target="metronome-tap" aria-describedby="tap-tempo-status" onClick={onTapTempo}>
                Tap Tempo
              </Button>
              <p id="tap-tempo-status" className={classes.tapStatus} role="status">
                {tapStatus}
              </p>
            </div>
            <Button
              data-tour-target="metronome-ramping"
              aria-pressed={isRamping}
              onClick={() => dispatch(mainActions.setMetronomeRamping(!isRamping))}
            >
              {isRamping ? "Ramping Metronome Enabled" : "Ramping Metronome Disabled"}
            </Button>
            <div className={classes.trims} role="group" aria-label="Adjust BPM">
              {[-5, -1, 1, 5].map(trimBtn)}
            </div>
          </div>
          <label className={classes.volume}>
            <span>Volume: {volume}%</span>
            <input
              type="range" min={0} max={100} value={volume}
              aria-label="Metronome volume"
              onChange={(event) => dispatch(mainActions.setMetronomeVolume(+event.target.value))}
            />
          </label>
          <div className={classes.practiceTimer} data-tour-target="metronome-timer">
            <span>Practice time: <span role="timer" aria-label="Practice time">{formatPracticeTime(practiceTimer.elapsed)}</span></span>
            <Button aria-label="Reset practice timer" onClick={practiceTimer.reset}>Reset</Button>
          </div>
        </section>
        <details className={classes.secondary}>
          <summary>Tempo presets</summary>
          <div className={classes.presets} role="group" aria-label="Tempo presets">
            {[40, 60, 80, 100, 120, 140, 160, 180].map(absBtn)}
          </div>
        </details>
        {audioState === "loading" && <p role="status" aria-label="Audio status">Loading metronome audio…</p>}
        {audioState === "error" && (
          <div>
            <p role="alert">Metronome audio could not load. Please try again.</p>
            <Button onClick={() => onRetryAudioCallback.current?.()}>Retry audio</Button>
          </div>
        )}
        <Metronome
          onAudioStateChange={onAudioStateChange}
          tempo={currentBpm}
          beatsPerMeasure={1}
          render={renderCallback}
          onStart={onMetronomeStartCallback}
          onStop={onMetronomeStopCallback}
          onTick={onMetromomeTick}
          beatVolume={volume / 100.0}
        />
      </Card>
      <Card className={classes.instructions}>
        <h2>How to use the Ramping Metronome</h2>
        <p>Choose a comfortable starting tempo using a preset or select the BPM display to enter a value from 10 to 400. Press Enter to confirm, or Escape to cancel. Leaving the input also cancels an unconfirmed edit.</p>
        <p>Press Tap Tempo at least twice in time with your desired beat, while stopped or playing. Further taps refine the estimate using up to four recent intervals. Tap Tempo covers 30–400 BPM; a pause longer than two seconds starts a new sequence.</p>
        <p>Press Start to begin. Enable ramping to increase the tempo by 0.02 BPM per beat, up to 400 BPM. The increase is gradual and largely imperceptible from one beat to the next, letting you concentrate on difficult passages and develop technical fluency. Disable ramping to practise at a steady tempo.</p>
        <p>Use −5, −1, +1 and +5 for manual adjustments, including while playing. Stop pauses playback at the current tempo; pressing Start again resumes at that tempo with a new beat. Leaving this page stops playback; returning uses your last selected starting tempo.</p>
        <p>Practice time adds up the time spent playing during this visit. It pauses on Stop and resumes on Start. Reset clears the total, including while playing, without interrupting playback. Leaving this page or refreshing starts a new practice session.</p>
      </Card>
      <RequestSupportCard />
      <Card>
        <BottomMenu />
      </Card>
    </div>
  );
};

export default MetronomePage;
